import { UseFormReturn } from 'react-hook-form'
import { WidgetFormValues, defaultWidgetValues } from '@/lib/widget-schema'
import { widgetThemes } from './widgetThemes'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Check } from 'lucide-react'
import { useTranslation } from '@/hooks/useTranslation'

interface ThemeSelectorProps {
	form: UseFormReturn<WidgetFormValues>
}

export function ThemeSelector({ form }: ThemeSelectorProps) {
	const { t } = useTranslation()
	const currentValues = form.watch()
	const isDarkThemeCard = (themeId: string) =>
		themeId === 'midnight' || themeId === 'dark-minimal'
	const openWidgetPreview = () => {
		if (typeof window !== 'undefined') {
			window.dispatchEvent(new CustomEvent('feedbask:open-widget-preview'))
		}
	}

	const handleThemeSelect = (themeConfig: Partial<WidgetFormValues>) => {
		const currentValues = form.getValues()
		const { position: _ignoredPosition, ...themeConfigWithoutPosition } =
			themeConfig
		const resetThemeLayoutFields: Partial<WidgetFormValues> = {
			brandingStyle: undefined,
			menuItemColors: undefined,
			menuItemStyle: undefined,
			animation: undefined,
			glassmorphism: undefined,
			layout: undefined,
			moduleNavigation: undefined,
		}

		// Create a merged configuration
		const mergedConfig = {
			...currentValues,
			...resetThemeLayoutFields,
			...themeConfigWithoutPosition,
		}

		// Reset form with merged values
		form.reset(mergedConfig, {
			keepDefaultValues: false,
			keepDirty: false,
			keepTouched: false,
		})
		openWidgetPreview()
	}

	const handleDefaultSelect = () => {
		const currentValues = form.getValues()

		// Reset theme-related fields to defaults while preserving other settings
		const defaultThemeConfig: Partial<WidgetFormValues> = {
			primaryColor: defaultWidgetValues.primaryColor,
			gradientEndColor: defaultWidgetValues.gradientEndColor,
			backgroundColor: defaultWidgetValues.backgroundColor,
			textColor: defaultWidgetValues.textColor,
			showHeader: defaultWidgetValues.showHeader,
			header_title: defaultWidgetValues.header_title,
			animation: defaultWidgetValues.animation,
			glassmorphism: undefined,
			layout: undefined,
			moduleNavigation: undefined,
			menuItemStyle: defaultWidgetValues.menuItemStyle,
			menuItemColors: undefined,
			brandingStyle: defaultWidgetValues.brandingStyle,
		}

		// Merge with current values to preserve non-theme settings
		const mergedConfig = { ...currentValues, ...defaultThemeConfig }

		// Reset form with merged values
		form.reset(mergedConfig, {
			keepDefaultValues: false,
			keepDirty: false,
			keepTouched: false,
		})
		openWidgetPreview()
	}

	const isDefaultActive = () => {
		// Check if current values match default theme values
		return (
			currentValues.primaryColor === defaultWidgetValues.primaryColor &&
			currentValues.gradientEndColor === defaultWidgetValues.gradientEndColor &&
			currentValues.backgroundColor === defaultWidgetValues.backgroundColor &&
			currentValues.textColor === defaultWidgetValues.textColor &&
			currentValues.showHeader === defaultWidgetValues.showHeader &&
			currentValues.menuItemStyle?.layout ===
				defaultWidgetValues.menuItemStyle?.layout &&
			currentValues.animation?.header ===
				defaultWidgetValues.animation?.header &&
			(!currentValues.glassmorphism || !currentValues.glassmorphism.enabled) &&
			!currentValues.menuItemColors
		)
	}

	const isThemeActive = (theme: (typeof widgetThemes)[0]) => {
		const config = theme.config
		const currentValues = form.watch()

		// Helper function to safely compare values with fallbacks
		const safeCompare = (current: any, expected: any, fallback: any = null) => {
			const currentVal = current ?? fallback
			const expectedVal = expected ?? fallback
			return currentVal === expectedVal
		}

		// Check basic appearance fields
		const basicChecks = {
			primaryColor: safeCompare(
				currentValues.primaryColor,
				config.primaryColor
			),
			gradientEndColor: safeCompare(
				currentValues.gradientEndColor,
				config.gradientEndColor,
				null
			),
			backgroundColor: safeCompare(
				currentValues.backgroundColor,
				config.backgroundColor
			),
			showHeader: safeCompare(
				currentValues.showHeader,
				config.showHeader,
				true
			),
		}
		const basicMatch = Object.values(basicChecks).every(Boolean)

		// Check theme-specific fields with more flexible comparison
		const themeSpecificChecks = {
			menuItemLayout: safeCompare(
				currentValues.menuItemStyle?.layout,
				config.menuItemStyle?.layout,
				'default'
			),
			glassmorphismEnabled: safeCompare(
				currentValues.glassmorphism?.enabled,
				config.glassmorphism?.enabled,
				false
			),
			layoutSpacing: safeCompare(
				currentValues.layout?.spacing,
				config.layout?.spacing,
				'default'
			),
			animationHeader: safeCompare(
				currentValues.animation?.header,
				config.animation?.header,
				'none'
			),
		}
		const themeSpecificMatch = Object.values(themeSpecificChecks).every(Boolean)

		const isActive = basicMatch && themeSpecificMatch

		return isActive
	}

	return (
		<div className="space-y-3">
			<h3 className="text-md font-semibold">{t('widgetBuilder.themeSelector.title')}</h3>
			<p className="text-sm text-muted-foreground">
				{t('widgetBuilder.themeSelector.description')}
			</p>
			<div className="grid grid-cols-2 md:grid-cols-3 gap-4">
				<Card
					className={cn(
						'relative',
						'border-2',
						isDefaultActive()
							? 'border-primary ring-2 ring-primary/20'
							: 'border-border'
					)}>
					{isDefaultActive() && (
						<div className="absolute top-2 right-2 z-0 pointer-events-none">
							<div className="rounded-full bg-primary p-1">
								<Check className="h-3 w-3 text-primary-foreground" />
							</div>
						</div>
					)}

					<div className="p-3 space-y-3">
						<div className="text-center">
							<p className="text-sm font-medium">{t('widgetBuilder.themeSelector.defaultTheme.name')}</p>
						</div>
						<Button type="button" variant="outline" className="w-full" onClick={handleDefaultSelect}>
							See a preview
						</Button>
					</div>
				</Card>

				{widgetThemes.map((theme) => {
					const isActive = isThemeActive(theme)
					const isDarkCard = isDarkThemeCard(theme.id)

					return (
						<Card
							key={theme.id}
							className={cn(
								'relative',
								'border-2',
								isActive
									? 'border-primary ring-2 ring-primary/20'
									: 'border-border'
							)}>
							{isActive && (
								<div className="absolute top-2 right-2 z-0 pointer-events-none">
									<div className="rounded-full bg-primary p-1">
										<Check className="h-3 w-3 text-primary-foreground" />
									</div>
								</div>
							)}

							<div
								className={cn('p-3 space-y-3 rounded-lg', isDarkCard && 'text-white')}
								style={
									isDarkCard
										? { backgroundColor: theme.config.backgroundColor || '#111827' }
										: undefined
								}>
								<div className="text-center">
									<p className="text-sm font-medium">
										{t(`widgetBuilder.themeSelector.themes.${theme.id}.name`, theme.name)}
									</p>
								</div>
								<Button
									type="button"
									variant={isDarkCard ? 'secondary' : 'outline'}
									className="w-full"
									onClick={() => handleThemeSelect(theme.config)}>
									See a preview
								</Button>
							</div>
						</Card>
					)
				})}
			</div>
		</div>
	)
}
